import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import solutionContent, { solutionCategories } from '../data/solutionContent';

const Solutions = () => {
  const navigate = useNavigate();
  const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-80px' },
    transition: { duration: 0.6, delay }
  });

  return (
    <div style={{ paddingTop: '5rem', color: 'var(--text-primary)' }}>
      {/* Hero */}
      <section style={{ padding: '6rem 5%', textAlign: 'center' }}>
        <motion.p {...fadeUp()} style={{ color: 'var(--accent-primary)', fontWeight: '700', fontSize: '0.9rem', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Our Solutions</motion.p>
        <motion.h1 {...fadeUp(0.1)} style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)', fontWeight: '800', marginBottom: '1.5rem', maxWidth: '800px', margin: '0 auto 1.5rem' }}>
          Institutional-Grade Strategies Across 12 Asset Classes
        </motion.h1>
        <motion.p {...fadeUp(0.2)} style={{ color: 'var(--text-secondary)', fontSize: '1.15rem', maxWidth: '700px', margin: '0 auto', lineHeight: '1.7' }}>
          From Private Equity to Carbon Credits, we provide UHNW investors and institutions with access to the world's most compelling risk-adjusted opportunities.
        </motion.p>
      </section>

      {/* Category Groups */}
      {solutionCategories.map((cat, catIdx) => (
        <section key={cat.name} style={{ padding: '2rem 5% 4rem', borderTop: catIdx > 0 ? '1px solid var(--border-color)' : 'none' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <motion.h2 {...fadeUp()} style={{ fontSize: '1.8rem', fontWeight: '800', marginBottom: '2rem', color: 'var(--text-primary)' }}>{cat.name}</motion.h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.5rem' }}>
              {cat.ids.map((solId, idx) => {
                const s = solutionContent[solId];
                if (!s) return null;
                return (
                  <motion.div
                    key={s.id}
                    {...fadeUp(idx * 0.08)}
                    onClick={() => navigate(`/solutions/${s.id}`)}
                    whileHover={{ y: -6, scale: 1.02, boxShadow: '0 12px 35px rgba(0,0,0,0.15)' }}
                    style={{
                      background: 'var(--bg-secondary)', padding: '2rem', borderRadius: '20px',
                      border: '1px solid var(--border-color)', cursor: 'pointer', transition: 'border-color 0.3s',
                      display: 'flex', flexDirection: 'column', justifyContent: 'space-between'
                    }}
                    onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; }}
                    onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border-color)'; }}
                  >
                    <div>
                      <div style={{ fontSize: '2.2rem', marginBottom: '1rem' }}>{s.emoji}</div>
                      <h3 style={{ fontSize: '1.2rem', fontWeight: '800', marginBottom: '0.3rem' }}>{s.title}</h3>
                      <p style={{ color: 'var(--accent-primary)', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{s.subtitle}</p>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: '1.6', marginBottom: '1.2rem' }}>
                        {s.desc.length > 150 ? s.desc.substring(0, 150) + '...' : s.desc}
                      </p>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', background: 'var(--bg-primary)', padding: '0.3rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border-color)' }}>
                        {s.investmentProfile.targetYield}
                      </span>
                      <span style={{ color: 'var(--accent-primary)', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.9rem' }}>
                        Explore <ArrowRight size={14} />
                      </span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section style={{ padding: '4rem 5% 6rem', textAlign: 'center' }}>
        <motion.div {...fadeUp()}>
          <h2 style={{ fontSize: '2rem', fontWeight: '800', marginBottom: '1rem' }}>Need a Tailored Strategy?</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '1.05rem', marginBottom: '2rem' }}>Our advisory team constructs bespoke, multi-asset portfolios for sophisticated investors.</p>
          <button onClick={() => navigate('/advisory')} style={{
            background: 'var(--accent-gradient)', padding: '1rem 2.5rem', borderRadius: '12px',
            color: '#000', fontWeight: '800', fontSize: '1rem', border: 'none', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 8px 25px rgba(252,213,53,0.3)'
          }}>Explore Advisory <ArrowRight size={18} /></button>
        </motion.div>
      </section>
    </div>
  );
};

export default Solutions;
